import * as React from 'react';

import { useGateValue } from '@coursera-web/statsig';
import {
  DescriptionPage_CourseType as DescriptionPageCourseType,
  DescriptionPage_ProductVariant as DescriptionPageProductVariant,
} from '__generated__/graphql-types';

import { useLocation, useParams } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { ExternalCoachAction } from 'bundles/ai-coach-platform/components/patterns/chat/ExternalCoachAction';
import {
  setChatSessionId,
  setChatSessionStorageKey,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import {
  getChatSessionStorageKey,
  persistentChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/sessionUtils';
import StyledCoachTriggerButtonWithAnimationOnMount from 'bundles/unified-common/components/coach-on-discovery/StyledCoachTriggerButtonWithAnimationOnMount';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import { getChatSessionKey } from 'bundles/unified-description-page-common/components/coach/coachSessionUtils';
import useXdpContextConfig from 'bundles/unified-description-page-common/components/coach/useXdpContextConfig';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import { useCoachCareerFitAssistantData } from 'bundles/unified-description-page-common/providers/specialization/CoachCareerFitAssistantProvider';

import _t from 'i18n!nls/unified-description-page-common';

// "Coach on all XDPs": V3 conversational discovery opening on a welcome message, with
// typed-message routing for follow-ups.
const COACH_MODE_ID = 'XDP_CAREER_FIT_ASSISTANT_V3';
// MERCH-3454: cheaper-model tryout for the welcome message only, opted into via the
// two-panel-xdp-new-llm-model gate. Follow-up (ROUTER) conversation stays on COACH_MODE_ID.
const NEW_LLM_COACH_MODE_ID = 'XDP_CAREER_FIT_ASSISTANT_V3_1';
const COACH_CONFIG = {
  buttonActionId: 'WELCOME_MESSAGE',
  routerActionId: 'ROUTER',
} as const;

const CoachButton = ({ className }: { className?: string }) => {
  const { coachCareerFitAssistantEnabled } = useCoachCareerFitAssistantData();
  const location = useLocation();
  const { productSlug } = useParams();
  const chatSessionKey = getChatSessionKey(isAuthenticatedUser(), productSlug || '');
  const newLlmModelEnabled = useGateValue('two-panel-xdp-new-llm-model');
  const coachModeId = newLlmModelEnabled ? NEW_LLM_COACH_MODE_ID : COACH_MODE_ID;
  const contextConfig = useXdpContextConfig({
    coachModeId,
    coachActionId: COACH_CONFIG.buttonActionId,
  });

  const { setCoachOpen, setContextConfig } = useCoachState();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: 'coach_trigger_button',
      linkName: `${coachModeId}_${COACH_CONFIG.buttonActionId}`.toLowerCase(),
    },
  });

  const specializationPageData = useSpecializationPageData();
  const coursePageData = useCoursePageData();

  if (!coachCareerFitAssistantEnabled || !contextConfig) {
    return null;
  }

  // Guided (RHYME_PROJECT) and portfolio (PROJECT) variants are both projects under the hood.
  const isProject =
    coursePageData?.courseType === DescriptionPageCourseType.Project ||
    coursePageData?.courseType === DescriptionPageCourseType.RhymeProject;

  const label = _t('Is this right for me?');
  let ariaLabel;
  if (specializationPageData?.productVariant === DescriptionPageProductVariant.ProfessionalCertificate) {
    ariaLabel = _t('Ask Coursera: is this certificate right for me?');
  } else if (specializationPageData) {
    ariaLabel = _t('Ask Coursera: is this specialization right for me?');
  } else if (isProject) {
    ariaLabel = _t('Ask Coursera: is this project right for me?');
  } else {
    ariaLabel = _t('Ask Coursera: is this course right for me?');
  }

  return (
    <div ref={visibilityTrackingRef}>
      <ExternalCoachAction
        size="small"
        key="coach"
        newSessionOnClick={false}
        skipSessionUpdate
        onClick={() => {
          // Pre-initialize Redux with the same auth+slug-scoped key that CoachChat uses, so
          // sendMessage (which reads chatSessionId from Redux) continues the correct session.
          // skipSessionUpdate prevents useCoachTriggerMessage from overwriting this with a
          // location-only key.
          const storageKey = getChatSessionStorageKey(location, chatSessionKey);
          setChatSessionStorageKey(storageKey);
          setChatSessionId(persistentChatSessionId.get(storageKey));

          const { message, ...coachChatContext } = contextConfig;
          setContextConfig({
            ...coachChatContext,
            config: {
              coachModeId: COACH_MODE_ID,
              coachActionId: COACH_CONFIG.routerActionId,
            },
          });
          setCoachOpen(true);
        }}
        aria-label={ariaLabel}
        className={className}
        label={label}
        enableTextStreaming
        enableLoggedOutMode
        {...contextConfig}
        message={label}
        customCoachTriggerButton={StyledCoachTriggerButtonWithAnimationOnMount}
      />
    </div>
  );
};

export default CoachButton;
